<template>
  <div class="lg-viz-picker" role="radiogroup" :aria-label="ariaLabel">
    <button
      v-for="opt in visible"
      :key="opt.type"
      type="button"
      class="lg-viz-opt"
      :class="{ active: opt.type === modelValue }"
      role="radio"
      :aria-checked="opt.type === modelValue"
      :aria-label="opt.label"
      :title="opt.title"
      @click="select(opt.type)"
    >
      <component :is="opt.icon" :size="16" aria-hidden="true" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  ChartLine,
  ChartColumn,
  ChartArea,
  ChartScatter,
  Hash,
  Gauge,
  ChartPie,
} from 'lucide-vue-next';

export type VizType = 'line' | 'bar' | 'area' | 'points' | 'counter' | 'gauge' | 'pie';

interface VizOption {
  type: VizType;
  label: string;
  title: string;
  icon: unknown;
}

// NOTE: `pie` is offered but LgChart does not render pie yet — treat it as a
// stub/placeholder viz until a pie renderer lands. Exclude it via `options`
// where a live chart can't honor it.
const ALL: VizOption[] = [
  { type: 'line', label: 'Line', title: 'Line', icon: ChartLine },
  { type: 'bar', label: 'Bar', title: 'Bar', icon: ChartColumn },
  { type: 'area', label: 'Stacked area', title: 'Stacked area', icon: ChartArea },
  { type: 'points', label: 'Points', title: 'Points', icon: ChartScatter },
  { type: 'counter', label: 'Counter', title: 'Counter (single value)', icon: Hash },
  { type: 'gauge', label: 'Gauge', title: 'Gauge', icon: Gauge },
  { type: 'pie', label: 'Pie', title: 'Pie (coming soon)', icon: ChartPie },
];

const props = withDefaults(
  defineProps<{
    modelValue: VizType;
    /** Restrict/reorder the offered types. Defaults to all seven. */
    options?: VizType[];
    ariaLabel?: string;
  }>(),
  { ariaLabel: 'Chart type' },
);

const emit = defineEmits<{ 'update:modelValue': [value: VizType] }>();

const visible = computed<VizOption[]>(() => {
  if (!props.options) return ALL;
  const order = props.options;
  return ALL.filter((o) => order.includes(o.type)).sort(
    (a, b) => order.indexOf(a.type) - order.indexOf(b.type),
  );
});

function select(type: VizType) {
  if (type !== props.modelValue) emit('update:modelValue', type);
}
</script>

<style scoped>
.lg-viz-picker {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 2px;
  background: var(--lg-bg-surface);
  border: 1px solid var(--lg-border-strong);
  border-radius: var(--lg-radius-md);
}
.lg-viz-opt {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--lg-control-sm);
  height: var(--lg-control-sm);
  padding: 0;
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text-dim);
  cursor: pointer;
  transition:
    background var(--lg-dur-fast) var(--lg-ease),
    color var(--lg-dur-fast) var(--lg-ease);
}
.lg-viz-opt:hover {
  background: var(--lg-bg-hover);
  color: var(--lg-text);
}
.lg-viz-opt.active {
  background: var(--lg-accent);
  color: var(--lg-accent-fg);
}
.lg-viz-opt:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
}
</style>
